import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import { Button, ButtonHTMLProps, ButtonProps } from '../../../main/components/button'
import { Input, InputHTMLProps, InputProps } from '../../../main/components/input'
import MapActionCreators from '../store/action-creators'
import { MapDataNode } from '../types'
import { useMapContext } from './Map'

const MIN_RESULTS_COUNT = 10
const SEARCH_ITEM_HEIGHT = 21

function filterItems(stocks: MapDataNode[] = [], input: string, limit: number = MIN_RESULTS_COUNT) {
  return stocks
    .filter(function (stock) {
      return stock.name.indexOf(input) === 0 || (stock.description || '').toUpperCase().indexOf(input) === 0
    })
    .sort(function (a, b) {
      if (a.name === b.name) {
        return 0
      }
      return a.name < b.name ? -1 : 1
    })
    .slice(0, limit)
}

export function useSearch() {
  const { treemap } = useMapContext()
  const listRef = React.useRef<HTMLUListElement>(null)
  const [numberOfItems, setNumberOfItems] = React.useState(MIN_RESULTS_COUNT)

  React.useEffect(() => {
    if (!treemap) return

    function getNumberOfItems(height: number = 0) {
      return Math.max(Math.floor(height / SEARCH_ITEM_HEIGHT), MIN_RESULTS_COUNT)
    }

    const onResize = throttle(
      function onResize() {
        setNumberOfItems(getNumberOfItems(listRef.current?.clientHeight ?? 0))
      },
      200,
      { leading: false, trailing: true }
    )

    window.addEventListener('resize', onResize)
    setNumberOfItems(getNumberOfItems(listRef.current?.clientHeight))

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [treemap])

  const [value, setValue] = React.useState('')
  const [results, setResults] = React.useState(filterItems(treemap?.nodes, '', numberOfItems))

  React.useEffect(() => {
    if (treemap) {
      setResults(filterItems(treemap.nodes, '', numberOfItems))
      setValue('')
    }
  }, [numberOfItems, treemap])

  const onInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value.toUpperCase()
      setValue(input)
      setResults(filterItems(treemap?.nodes, input, numberOfItems))
    },
    [numberOfItems, treemap?.nodes]
  )

  const onItemClick = React.useCallback(
    (node: MapDataNode) => {
      if (!treemap) return

      const cx = (node.x + node.x + node.dx) / 2,
        cy = (node.y + node.y + node.dy) / 2,
        diffX = cx - treemap.width / 2,
        diffY = cy - treemap.height / 2

      MapActionCreators.zoomAndTranslate(treemap, treemap.getLastZoomLevel(), cx + diffX, cy + diffY)
      MapActionCreators.setHoveredNode(treemap.mapNodeId, node)
    },
    [treemap]
  )

  const onResultMouseLeave = React.useCallback(() => {
    if (!treemap) return
    MapActionCreators.setHoveredNode(treemap.mapNodeId, undefined)
  }, [treemap])

  return {
    listRef,
    value,
    results,
    onInputChange,
    onItemClick,
    onResultMouseLeave,
  }
}

export function SearchInput(props: InputProps & Omit<InputHTMLProps, 'ref'>) {
  return (
    <Input
      theme="none"
      rounding="none"
      placeholder="Quick search ticker"
      leftContent="search"
      onChange={props.onChange}
      value={props.value}
      {...props}
    />
  )
}

interface SearchResultsListProps extends React.PropsWithoutRef<React.HTMLProps<HTMLDivElement>> {
  listClass?: string
}

export function SearchResultsList({
  ref,
  children,
  className,
  listClass,
  ...props
}: SearchResultsListProps & { ref: React.ForwardedRef<HTMLUListElement> }) {
  return (
    <div {...props} className={classnames('min-h-60 flex-1 overflow-hidden', className)}>
      <ul ref={ref} className={classnames('h-0 min-h-full divide-y', listClass)}>
        {children}
      </ul>
    </div>
  )
}

interface SearchResultProps extends ButtonProps, Omit<ButtonHTMLProps, 'onClick' | 'onMouseLeave'> {
  result: MapDataNode
  onClick: (node: MapDataNode) => void
  onMouseLeave: (e: React.MouseEvent) => void
}

export function SearchResult({ result, onClick, onMouseLeave, ...props }: Omit<SearchResultProps, 'as' | 'ref'>) {
  return (
    <li onMouseLeave={onMouseLeave}>
      <Button
        {...props}
        theme="transparent"
        size="none"
        className={classnames(props.className, 'h-5 w-full')}
        contentClass={classnames(props.className, 'flex overflow-hidden text-3xs')}
        onClick={() => onClick(result)}
      >
        <span className="w-11 shrink-0 font-bold">{result.name}</span>
        <span className="truncate">{result.description}</span>
      </Button>
    </li>
  )
}
